import { Select as BaseSelect } from "@base-ui-components/react/select";
import { ChevronDown, Loader2 } from "lucide-react";
import {
  iconClasses,
  positionerClasses,
  scrollDownClasses,
  scrollUpClasses,
  selectPopupClasses,
  selectRootClasses,
  selectTriggerClasses,
} from "./Select.style";
import Option from "@/Option";
import capitalize from "lodash/capitalize";
import words from "lodash/words";
import {
  errorClasses,
  helpTextClasses,
  labelClasses,
} from "@/Field/Field.style";
import { Field } from "@base-ui-components/react/field";
import React, { useMemo } from "react";
import { flattenChildren } from "@/utils/helpers";

type SelectProps = Omit<
  BaseSelect.Root.Props<any, any>,
  "onValueChange" | "items"
> & {
  label?: string;
  onChange?: (value: any, event?: Event) => void;
  options?: BaseSelect.Root.Props<any>["items"];
  align?: "center" | "start" | "end";
  className?: string;
  valueRender?: BaseSelect.Value.Props["render"];
  error?: string;
  helpText?: string;
  colSpan?: 1 | 2 | 3 | 4 | 5 | 6;
  loading?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  noLabel?: boolean;
};

const Select = (props: SelectProps) => {
  const {
    value,
    onChange,
    options,
    required,
    label,
    name,
    align,
    colSpan,
    className,
    children,
    valueRender,
    error,
    helpText,
    loading,
    disabled,
    invalid,
    noLabel = false,
    ...rest
  } = props;

  const capitalizeName = capitalize(words(name).join(" "));

  const optionChildren = flattenChildren(children);
  const hasOptionChildren = optionChildren.length > 0;
  const items = hasOptionChildren
    ? optionChildren.map((child) => {
        const optionChild = child as React.ReactElement<any>;
        return {
          label: optionChild.props.children,
          value: optionChild.props.value,
          ...optionChild.props,
        };
      })
    : undefined;

  // Create a key that changes when options or loading changes
  const optionsKey = hasOptionChildren
    ? optionChildren
        .map((c) => (c as React.ReactElement<any>).props.value)
        .join(",")
    : Array.isArray(options)
      ? options.map((o) => o.value).join(",")
      : "";
  const rootKey = `${optionsKey}-${loading}`;

  const renderOptions = useMemo(() => {
    if (hasOptionChildren) {
      return items;
    }
    return options;
  }, [options, hasOptionChildren, items]);

  function renderValue(value: any) {
    if (Array.isArray(value) && value.length === 0) {
      return "Select an option...";
    }
    if (!Array.isArray(value) && !rest.multiple) {
      return Array.isArray(renderOptions)
        ? renderOptions.find(
            (item: { value: any; label: any }) => item.value == value
          )?.label
        : undefined;
    }
    if (Array.isArray(value) && !rest.multiple) {
      return Array.isArray(renderOptions)
        ? renderOptions.find(
            (item: { value: any; label: any }) => item.value == value
          )?.label
        : undefined;
    }

    return Array.isArray(renderOptions) && rest.multiple
      ? renderOptions
          .filter((opt) => value?.includes(opt.value))
          .map((opt) => opt.label)
          .join(", ")
      : undefined;
  }

  return (
    <Field.Root
      invalid={Boolean(error) || invalid}
      className={selectRootClasses({ colSpan, className: className })}
      validationMode="onBlur"
    >
      {(label || capitalizeName) && (
        <Field.Label
          className={labelClasses({ required, hidden: noLabel })}
          autoFocus={false}
        >
          {label || capitalizeName}
        </Field.Label>
      )}

      <BaseSelect.Root
        key={rootKey}
        modal={false}
        onValueChange={(value, eventDetail) =>
          onChange?.(value, eventDetail.event)
        }
        required={required}
        name={name}
        value={value}
        disabled={loading || disabled}
        {...rest}
        items={hasOptionChildren ? items : options}
      >
        <BaseSelect.Trigger
          className={selectTriggerClasses({ className: className })}
          disabled={loading || disabled}
        >
          <BaseSelect.Value render={valueRender} className="truncate">
            {renderValue}
          </BaseSelect.Value>
          <BaseSelect.Icon className={iconClasses()}>
            {loading ? (
              <Loader2 className="animate-spin" size={20} />
            ) : (
              <ChevronDown size={20} />
            )}
          </BaseSelect.Icon>
        </BaseSelect.Trigger>
        <BaseSelect.Portal>
          <BaseSelect.Positioner
            className={positionerClasses()}
            sideOffset={8}
            align={align}
            alignItemWithTrigger={false}
          >
            <BaseSelect.ScrollUpArrow className={scrollUpClasses()} />
            <BaseSelect.Popup className={selectPopupClasses()}>
              {hasOptionChildren
                ? optionChildren
                : Array.isArray(options)
                  ? options.map((option) => (
                      <Option
                        key={option.value}
                        value={option.value}
                        label={
                          option.label !== undefined
                            ? String(option.label)
                            : undefined
                        }
                      />
                    ))
                  : children}
            </BaseSelect.Popup>
            <BaseSelect.ScrollDownArrow className={scrollDownClasses()} />
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>

      {error ? (
        <Field.Error className={errorClasses({})} match>
          {error}
        </Field.Error>
      ) : (
        <Field.Error className={errorClasses({})} />
      )}

      {helpText && (
        <Field.Description className={helpTextClasses({})}>
          {helpText}
        </Field.Description>
      )}
    </Field.Root>
  );
};
Select.displayName = "Select";
export default Select;
